const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

function getToken(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem("coagent_console_token");
}

export function setToken(t: string | null): void {
  if (typeof localStorage === "undefined") return;
  if (t) localStorage.setItem("coagent_console_token", t);
  else localStorage.removeItem("coagent_console_token");
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("Accept", "application/json");
  if (init?.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const tok = getToken();
  if (tok) headers.set("Authorization", `Bearer ${tok}`);
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  health: () => req<{ status: string; checks: Record<string, string> }>("/health"),
  dashboard: () => req<{ servers: unknown[]; coagent: unknown }>("/dashboard"),
  tasks: {
    list: (params?: Record<string, string>) => {
      const q = params ? "?" + new URLSearchParams(params).toString() : "";
      return req<{ tasks: Task[] }>(`/tasks${q}`);
    },
    create: (t: Partial<Task>) => req<{ task: Task }>("/tasks", { method: "POST", body: JSON.stringify(t) }),
    update: (id: string, patch: Partial<Task>) =>
      req<{ task: Task }>(`/tasks/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    remove: (id: string) => req<{ deleted: boolean }>(`/tasks/${id}`, { method: "DELETE" }),
    bulkRemove: (ids: string[]) =>
      req<{ deleted: number; ids: string[] }>("/tasks/bulk-delete", {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
    bulkRemoveBy: (filter: { status?: Task["status"]; source?: Task["source"] }) =>
      req<{ deleted: number }>("/tasks/bulk-delete-by", {
        method: "POST",
        body: JSON.stringify(filter),
      }),
    summary: () => req<{ summary: Array<{ status: string; count: number }> }>("/tasks/summary/by-status"),
    summaryByStage: () =>
      req<{ summary: Array<{ stage: string; count: number }> }>("/tasks/summary/by-stage"),
    bulkStage: (taskIds: string[], stage: TaskStage) =>
      req<{ updated: number; ids: string[] }>("/tasks/bulk-stage", {
        method: "POST",
        body: JSON.stringify({ taskIds, stage }),
      }),
    dispatch: (taskIds: string[], agent: DispatchAgent, mode: "ide" | "headless" = "ide") =>
      req<{ dispatched: number; skipped: string[]; queueFiles: string[] }>("/tasks/dispatch", {
        method: "POST",
        body: JSON.stringify({ taskIds, agent, mode }),
      }),
    feedback: (id: string, feedback: "positive" | "negative" | "neutral", note?: string) =>
      req<{ ok: boolean; task: Task; run_id: string | null; run_stamped: boolean; blocked: boolean }>(
        `/tasks/${id}/feedback`,
        { method: "POST", body: JSON.stringify({ feedback, note }) },
      ),
  },
  runs: {
    list: (params?: Record<string, string>) => {
      const q = params ? "?" + new URLSearchParams(params).toString() : "";
      return req<{ runs: AgentRun[] }>(`/runs${q}`);
    },
    scoreboard: () =>
      req<{ scoreboard: ScoreboardRow[]; window_days: number }>("/runs/scoreboard"),
    feedback: (id: string, feedback: "positive" | "negative" | "neutral", note?: string) =>
      req<{ ok: boolean }>(`/runs/${id}/feedback`, {
        method: "POST",
        body: JSON.stringify({ feedback, note }),
      }),
  },
  autopilotFreeze: {
    status: () =>
      req<{ frozen: boolean; last_change_at: string | null; last_change_by: string | null }>(
        "/autopilot/status",
      ),
    freeze: () =>
      req<{ ok: boolean; frozen: boolean; last_change_at: string | null; last_change_by: string | null }>(
        "/autopilot/freeze",
        { method: "POST", body: "{}" },
      ),
    unfreeze: () =>
      req<{ ok: boolean; frozen: boolean; last_change_at: string | null; last_change_by: string | null }>(
        "/autopilot/unfreeze",
        { method: "POST", body: "{}" },
      ),
  },
  repos: () => req<{ repos: GiteaRepo[] }>("/repos"),
  branches: (repo?: string) => req<{ branches: Branch[]; repo: string }>(`/branches${repo ? `?repo=${encodeURIComponent(repo)}` : ""}`),
  pulls: (repo?: string, state: string = "open") =>
    req<{ pulls: PullRequest[]; repo: string }>(`/pull-requests?${new URLSearchParams({ ...(repo ? { repo } : {}), state }).toString()}`),
  clients: (env: "prod" | "stage" = "prod") => req<{ companies: unknown[] }>(`/clients?env=${env}`),
  signals: (params: Record<string, string> = {}) => {
    const q = new URLSearchParams(params).toString();
    return req<{ signals: unknown[] }>(`/signals${q ? "?" + q : ""}`);
  },
  chatStatus: () => req<{ ok: boolean; reason?: string; auth_present?: boolean; vpn_set?: boolean }>("/chat/status"),
  chatLoginStart: () =>
    req<{ url: string } | { error: string; reason?: string; debug?: string }>("/chat/login/start", { method: "POST", body: "{}" }),
  chatLoginFinish: (code: string) =>
    req<{ ok: boolean; reason?: string; debug?: string }>("/chat/login/finish", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  chatLoginCancel: () => req<{ cancelled: true }>("/chat/login/cancel", { method: "POST", body: "{}" }),
  chatLogout: () => req<{ ok: boolean; note?: string }>("/chat/login/logout", { method: "POST", body: "{}" }),
  chatSend: (msg: { message: string; session_id?: string; task_id?: string }) =>
    req<{ session_id: string; accepted: boolean }>("/chat/send", { method: "POST", body: JSON.stringify(msg) }),
  chatSessions: () => req<{ sessions: ChatSession[] }>("/chat/sessions"),
  chatMessages: (id: string) => req<{ messages: ChatMessage[] }>(`/chat/sessions/${id}/messages`),
  settings: () => req<Settings>("/settings"),
  flags: {
    list: () => req<{ flags: FeatureFlag[] }>("/flags"),
    get: (key: string) =>
      req<{ flag: FeatureFlag; history: FeatureFlagHistoryEntry[] }>(`/flags/${encodeURIComponent(key)}`),
    create: (body: { key: string; description?: string; default_enabled?: boolean }) =>
      req<{ flag: FeatureFlag }>("/flags", { method: "POST", body: JSON.stringify(body) }),
    update: (key: string, patch: Partial<FeatureFlag> & { reason?: string }) =>
      req<{ flag: FeatureFlag }>(`/flags/${encodeURIComponent(key)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    remove: (key: string) =>
      req<{ deleted: boolean }>(`/flags/${encodeURIComponent(key)}`, { method: "DELETE" }),
    resolve: (key: string, companyId: string) =>
      req<{
        key: string;
        company_id: string;
        live: boolean;
        enabled: boolean;
        reason: string;
        bucket?: number;
        cohorts?: string[];
      }>(`/flags/${encodeURIComponent(key)}/resolve?company_id=${encodeURIComponent(companyId)}`),
  },
  orchestrator: {
    config: () => req<{ config: Record<string, unknown> }>("/orchestrator/config"),
    setConfig: (key: string, value: unknown) =>
      req<{ ok: boolean; config: Record<string, unknown> }>("/orchestrator/config", {
        method: "PATCH",
        body: JSON.stringify({ key, value }),
      }),
    sessions: () => req<{ sessions: OrchestratorSession[] }>("/orchestrator/sessions"),
    messages: (id: string) =>
      req<{ messages: OrchestratorMessage[] }>(`/orchestrator/sessions/${id}/messages`),
    listMemory: (category?: string) => {
      const q = category ? `?category=${encodeURIComponent(category)}` : "";
      return req<{ memories: OrchestratorMemory[] }>(`/orchestrator/memory${q}`);
    },
    addMemory: (category: string, content: string, source = "manual") =>
      req<{ memory: OrchestratorMemory }>("/orchestrator/memory", {
        method: "POST",
        body: JSON.stringify({ category, content, source }),
      }),
    deleteMemory: (id: number) =>
      req<{ deleted: boolean }>(`/orchestrator/memory/${id}`, { method: "DELETE" }),
    autopilotLog: (limit = 50) =>
      req<{ log: OrchestratorAutopilotLogEntry[] }>(`/orchestrator/autopilot/log?limit=${limit}`),
    runAutopilotNow: () =>
      req<{ ok: boolean }>("/orchestrator/autopilot/run-now", { method: "POST", body: "{}" }),
  },
  cohorts: {
    list: () => req<{ cohorts: Cohort[] }>("/cohorts"),
    get: (name: string) => req<{ cohort: Cohort; members: CohortMember[] }>(`/cohorts/${encodeURIComponent(name)}`),
    addMembers: (name: string, companyIds: string[], companyNames?: Record<string, string>) =>
      req<{ added: number; requested: number; duplicates: number }>(`/cohorts/${encodeURIComponent(name)}/members`, {
        method: "POST",
        body: JSON.stringify({ company_ids: companyIds, company_names: companyNames }),
      }),
    removeMember: (name: string, companyId: string) =>
      req<{ deleted: boolean }>(
        `/cohorts/${encodeURIComponent(name)}/members/${encodeURIComponent(companyId)}`,
        { method: "DELETE" },
      ),
  },
  pipeline: {
    recent: (limit = 20) =>
      req<{ items: PipelineRecentRow[]; count: number; error?: string }>(
        `/pipeline/recent?limit=${limit}`,
      ),
    state: (taskId: string) =>
      req<PipelineStateResponse>(`/pipeline/state/${encodeURIComponent(taskId)}`),
    start: (taskId: string) =>
      req<{ task_id: string; state: PipelineState; started_at: string }>("/pipeline/start", {
        method: "POST",
        body: JSON.stringify({ taskId }),
      }),
    reset: (taskId: string) =>
      req<{ task_id: string; state: PipelineState }>("/pipeline/reset", {
        method: "POST",
        body: JSON.stringify({ taskId }),
      }),
    advance: (taskId: string) =>
      req<{ task_id: string; state: PipelineState }>("/pipeline/advance", {
        method: "POST",
        body: JSON.stringify({ taskId }),
      }),
  },
};

export type PipelineState =
  | "pending"
  | "classifying"
  | "planning"
  | "executing"
  | "reviewing"
  | "revising"
  | "deploying"
  | "completed"
  | "escalated";

export interface PipelineRecentRow {
  id: string;
  title: string;
  stage: string | null;
  updated_at: string;
  state: PipelineState | null;
  tier: number | null;
  verdict: string | null;
  escalation_reason: string | null;
  started_at: string | null;
}

export interface PipelineHistoryEntry {
  at: string;
  from: PipelineState | null;
  to: PipelineState;
  meta?: Record<string, unknown>;
}

export interface PipelineStateResponse {
  task_id: string;
  state: PipelineState;
  started_at: string | null;
  updated_at: string | null;
  tier: number | null;
  history: PipelineHistoryEntry[];
}

export type TaskStage =
  | "inbox"
  | "triage"
  | "in_progress"
  | "review"
  | "deploy"
  | "done"
  | "blocked";

export type TaskSeverity = "green" | "yellow" | "red";

export type TaskAgent = "claude" | "codex" | "qwen" | "auto" | null;

export type DispatchAgent = "claude" | "codex" | "auto";

export interface StageHistoryEntry {
  stage: TaskStage;
  at: string;
  by?: string | null;
  note?: string | null;
}

export interface TaskFeedback {
  value: "positive" | "negative" | "neutral";
  note?: string | null;
  at: string;
  by?: string | null;
  run_id?: string | null;
}

export interface Task {
  id: string;
  title: string;
  description: string;
  status: "todo" | "in_progress" | "review" | "done";
  priority: "low" | "medium" | "high" | "urgent";
  source: "manual" | "autopilot" | "logwatch" | "signal" | "tg" | "pr" | "imported";
  assignee: string | null;
  metadata: Record<string, unknown> & { feedback?: TaskFeedback };
  created_at: string;
  updated_at: string;
  // Phase 1a pipeline fields
  stage?: TaskStage;
  severity?: TaskSeverity | null;
  agent_assigned?: TaskAgent;
  related_pr_url?: string | null;
  cost_cents_spent?: number;
  stage_history?: StageHistoryEntry[];
  // Phase 10: cross-agent consensus (Tier-1 auto-merge). Surfaced by backend
  // when the latest agent_run row has a verification_result.
  consensus?: {
    decision: "APPROVE" | "DEMAND_CANARY" | "REJECT";
    verifier_agent?: string | null;
    verifier_notes?: string | null;
  } | null;
}

export interface GiteaRepo {
  full_name: string;
  description?: string;
  default_branch: string;
  open_issues_count?: number;
  updated_at?: string;
}

export interface Branch {
  name: string;
  commit_sha: string;
  commit_message?: string;
  commit_date?: string;
  protected?: boolean;
}

export interface PullRequest {
  number: number;
  title: string;
  state: string;
  user: string;
  base_branch: string;
  head_branch: string;
  mergeable?: boolean;
  url: string;
  updated_at: string;
}

export interface ChatSession {
  id: string;
  task_id: string | null;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export interface ChatMessage {
  id: number;
  session_id: string;
  role: "user" | "assistant" | "system" | "tool";
  content: string;
  created_at: string;
}

export interface FeatureFlag {
  key: string;
  description: string | null;
  default_enabled: boolean;
  enabled_cohorts: string[];
  enabled_companies: string[];
  disabled_companies: string[];
  percent_rollout: number;
  created_at: string;
  updated_at: string;
  metadata: Record<string, unknown>;
  cohort_member_count?: number;
}

export interface FeatureFlagHistoryEntry {
  id: number;
  action: string;
  old_value: unknown;
  new_value: unknown;
  actor: string | null;
  reason: string | null;
  created_at: string;
}

export interface Cohort {
  name: string;
  description: string | null;
  size_target: number | null;
  created_at: string;
  metadata: Record<string, unknown>;
  member_count?: number;
}

export interface CohortMember {
  company_id: string;
  company_name: string | null;
  assigned_at: string;
  assigned_by: string | null;
}

export interface Settings {
  env: string;
  panel_url: string;
  autopilot: { enabled: boolean; mode: string; cron: string };
  logwatch: { enabled: boolean; interval_sec: number; sources: string[] };
  integrations: Record<string, "configured" | "missing">;
  proxy: { https_proxy: "set" | "none" };
}

export interface OrchestratorSession {
  id: string;
  title: string | null;
  created_at: string;
  last_message_at: string;
  message_count?: number;
}

export interface OrchestratorMessage {
  id: number;
  session_id: string;
  role: "user" | "assistant";
  content: string;
  events: unknown[];
  created_at: string;
}

export interface OrchestratorMemory {
  id: number;
  category: "user_preference" | "goal" | "fact" | "decision";
  content: string;
  source: string | null;
  created_at: string;
}

export interface OrchestratorAutopilotLogEntry {
  id: number;
  cycle_id: string;
  mode: string;
  balance: number;
  triaged_count: number;
  dispatched_ids: string[];
  notes: string | null;
  created_at: string;
}

// Phase 8a: Outcomes ledger row.
export interface AgentRun {
  id: string;
  task_id: string | null;
  agent: string;
  model: string | null;
  trigger: string;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  outcome: string | null;
  cost_cents_estimated: number;
  tokens_input: number | null;
  tokens_output: number | null;
  pr_url: string | null;
  pr_merged: boolean | null;
  pr_reverted: boolean;
  reopened_within_7d: boolean | null;
  user_feedback: "positive" | "negative" | "neutral" | null;
  user_feedback_note: string | null;
  user_feedback_at: string | null;
  error_text: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface ScoreboardRow {
  agent: string;
  model: string;
  source: string;
  severity: string;
  total: number;
  success: number;
  failed: number;
  stalled: number;
  feedback_positive: number;
  feedback_negative: number;
  reopened: number;
  avg_duration_ms: number | null;
  total_cost_cents: number;
  success_rate: number;
}

export function wsUrl(): string {
  const base = API_BASE.startsWith("http") ? API_BASE : window.location.origin + API_BASE;
  return base.replace(/^http/, "ws") + "/ws";
}
